import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { PurchaseRequest } from '@teo/shared';
import { api } from '../api/client';
import { Badge } from '../components/Panel';
import { Modal } from '../components/Overlay';
import { EmptyState, TableSkeleton } from '../components/States';
import { useSubmit } from '../lib/useAsync';
import { useToast } from '../lib/useToast';
import { releaseKey, requestKey } from '../lib/idempotency';
import { formatDate, formatMoney, formatNumber } from '../lib/format';
import type { Language } from '../i18n';

const TONE = { PENDING: 'amber', AUTHORIZED: 'blue', RECEIVED: 'green', REJECTED: 'red' } as const;

/** Nombre del producto pedido, o el propuesto cuando todavía no existe. */
export function purchaseLabel(r: PurchaseRequest, t: (k: string, o?: Record<string, unknown>) => string): string {
  if (r.ref) return [r.ref, r.product_name].filter(Boolean).join(' · ');
  return t('purchases.proposedLabel', { name: String(r.proposed?.name ?? '') });
}

/** Tabla de solicitudes, compartida por la vista del local y la de autorizaciones. */
export function PurchaseTable({
  rows, loading, showLocation, actions,
}: {
  rows: PurchaseRequest[];
  loading: boolean;
  showLocation: boolean;
  actions?: (r: PurchaseRequest) => ReactNode;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage as Language;
  if (!loading && rows.length === 0) return <EmptyState icon="inbox" title={t('purchases.pendingNone')} />;
  return (
    <div className="tablewrap">
      <table>
        <thead>
          <tr>
            <th>{t('purchases.cols.date')}</th>
            <th>{t('purchases.cols.doc')}</th>
            {showLocation ? <th>{t('purchases.cols.location')}</th> : null}
            <th>{t('purchases.cols.product')}</th>
            <th className="num">{t('purchases.cols.qty')}</th>
            <th className="num">{t('purchases.cols.cost')}</th>
            <th>{t('purchases.cols.requestedBy')}</th>
            <th>{t('purchases.cols.decidedBy')}</th>
            <th className="num">{t('purchases.cols.received')}</th>
            <th>{t('common.status')}</th>
            <th></th>
          </tr>
        </thead>
        <tbody id="purchaseBody">
          {loading ? <TableSkeleton cols={showLocation ? 11 : 10} rows={3} /> : null}
          {rows.map((r) => (
            <tr key={r.document_id} data-request={r.doc_no} data-status={r.status}>
              <td>{formatDate(r.created_at, lang)}</td>
              <td><b>{r.doc_no}</b></td>
              {showLocation ? <td>{r.location_name}</td> : null}
              <td>
                <b>{purchaseLabel(r, t)}</b>
                {r.note ? <div className="tiny muted">{r.note}</div> : null}
                {r.decision_note ? <div className="tiny muted">{r.decision_note}</div> : null}
              </td>
              <td className="num">{formatNumber(r.qty, lang)}</td>
              <td className="num">{formatMoney(r.estimated_cost, lang)}</td>
              <td>{r.requested_by_name ?? ''}</td>
              <td>{r.decided_by_name ?? ''}</td>
              <td className="num" data-col="received">
                {r.received_qty == null ? '' : t('purchases.authorizedOf', { received: formatNumber(r.received_qty, lang), qty: formatNumber(r.qty, lang) })}
                {r.received_doc_no ? <div className="tiny muted">{r.received_doc_no}</div> : null}
              </td>
              <td><Badge tone={TONE[r.status]}>{t(`purchases.status.${r.status}`)}</Badge></td>
              <td>{actions?.(r)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/** Recepción física de una compra autorizada: aquí sí entra al inventario. */
export function ReceivePurchaseModal({ target, onClose, onDone }: { target: PurchaseRequest | null; onClose: () => void; onDone: () => void }) {
  const { t } = useTranslation();
  const { show, showError } = useToast();
  const { busy, run } = useSubmit();
  const [qty, setQty] = useState('');
  const [cost, setCost] = useState('');
  const [supplier, setSupplier] = useState('');

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!target) return;
    const scope = `compra:recibir:${target.document_id}`;
    void run(async () => {
      try {
        const r = await api.receivePurchase(requestKey(scope), target.document_id, { qty: Number(qty), unit_cost: Number(cost) || 0, supplier });
        releaseKey(scope);
        setQty(''); setCost(''); setSupplier('');
        onClose();
        show(t('purchases.receivedDone', { doc: r.doc_no }));
        onDone();
      } catch (err) { showError(err); }
    });
  };

  return (
    <Modal open={!!target} title={t('purchases.receiveTitle', { doc: target?.doc_no ?? '' })} help={t('purchases.receiveHelp')} onClose={onClose}>
      <form className="formgrid" id="receivePurchaseForm" onSubmit={submit}>
        <div className="field">
          <label htmlFor="rpQty">{t('purchases.receivedQty')}</label>
          <input id="rpQty" type="number" min={1} max={target?.qty ?? 1} required value={qty} onChange={(e) => setQty(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="rpCost">{t('purchases.realCost')}</label>
          <input id="rpCost" type="number" min={0} required value={cost} onChange={(e) => setCost(e.target.value)} />
        </div>
        <div className="field span2">
          <label htmlFor="rpSupplier">{t('purchases.supplier')}</label>
          <input id="rpSupplier" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
        </div>
        <button className="btn black span2" type="submit" id="rpSubmit" disabled={busy || Number(qty) <= 0}>
          {t('purchases.receiveSubmit')}
        </button>
      </form>
    </Modal>
  );
}
